import React from 'react';
import { useAppDispatch } from '../../../../../redux/hooks';
import { ModalSliceType, openModal } from '../../../../../redux/slices/modal';
import { IDashboardTemplateFieldsFragment } from '../../../../../utils/apollo/graphqlSchema';
import ActionsMenu from '../../../../../components/ActionsMenu';
import ThinDivider from '../../../../../components/ThinDivider';
import { clipboardDashboardLink } from '../../../../../components/SharePermissionsWidget/clipboardDashboardLink';
import { useDashboardEntityPermission } from '../../../../../utils/hooks/permissionHooks/useDashboardEntityPermission';
import toastMessage from '../../../../../utils/toastMessage';
import { useFavoriteDashboardTemplate } from './utils/useFavoriteDashboardTemplate';
import { DashboardNavSectionType } from '../../../types';
import { SingleSelectOption } from '../../../../../components/Dropdown/SingleSelect';
import styled from 'styled-components';

interface IProps {
  template: IDashboardTemplateFieldsFragment;
  onOpen: () => void;
  onClose: () => void;
  onSelectOption: () => void;
  variant: DashboardNavSectionType;
}

const StyledActionsMenu = styled(ActionsMenu)`
  flex: 0 0 auto;
  display: inline-flex;
`;

const DashboardTemplateActions = ({
  template,
  onOpen,
  onClose,
  onSelectOption,
  variant,
}: IProps) => {
  const dispatch = useAppDispatch();
  const toggleFavoriteDashboard = useFavoriteDashboardTemplate({
    baseId: template.baseId,
    isFavorite: template.isFavorite,
  });

  const { dashboardEntityPermissions, dashboardEntityPermissionsHint } =
    useDashboardEntityPermission(template);

  // Since DashboardNavItem is a NavLink, we need to prevent the default click event
  // to avoid the page from reloading
  // Add this to all new options onClick handlers
  const handlePreventDefaultClick = (event: React.MouseEvent<HTMLElement>) => {
    event.preventDefault();
    onSelectOption();
  };

  const handleCopyChartLink = (event: React.MouseEvent<HTMLElement>) => {
    handlePreventDefaultClick(event);
    navigator.clipboard.writeText(clipboardDashboardLink(template.baseId));

    toastMessage({
      message: `Dashboard Link Copied!`,
      type: 'info',
    });
  };

  const handlePublish = (event: React.MouseEvent<HTMLElement>) => {
    handlePreventDefaultClick(event);
    dispatch(
      openModal({
        type: ModalSliceType.DashboardPublish,
        data: template,
      }),
    );
  };

  const handleDuplicate = (event: React.MouseEvent<HTMLElement>) => {
    handlePreventDefaultClick(event);
    dispatch(
      openModal({
        type: ModalSliceType.DashboardDuplicate,
        data: {
          type: 'template',
          template,
        },
      }),
    );
  };

  const handleDelete = (event: React.MouseEvent<HTMLElement>) => {
    handlePreventDefaultClick(event);
    dispatch(
      openModal({ type: ModalSliceType.DashboardDelete, data: template }),
    );
  };

  const handleUnpublish = (event: React.MouseEvent<HTMLElement>) => {
    handlePreventDefaultClick(event);
    dispatch(
      openModal({ type: ModalSliceType.DashboardUnpublish, data: template }),
    );
  };

  const handleMove = (event: React.MouseEvent<HTMLElement>) => {
    handlePreventDefaultClick(event);
    dispatch(openModal({ type: ModalSliceType.DashboardMove, data: template }));
  };

  const handleToggleFavorite = (event: React.MouseEvent<HTMLElement>) => {
    handlePreventDefaultClick(event);
    toggleFavoriteDashboard();
  };

  return (
    <StyledActionsMenu
      onClick={(event) => {
        event.preventDefault();
        event.stopPropagation();
      }}
      onOpen={onOpen}
      onClose={onClose}
    >
      <SingleSelectOption
        onClick={handleCopyChartLink}
        iconName="link-regular"
        label={'Copy URL'}
      />
      <SingleSelectOption
        iconName={template.isFavorite ? 'star-crossed' : 'star'}
        label={template.isFavorite ? 'Unfavorite' : 'Favorite'}
        onClick={handleToggleFavorite}
      />
      {(variant === DashboardNavSectionType.My ||
        (variant === DashboardNavSectionType.Shared &&
          !template.isPublished)) && (
        <SingleSelectOption
          onClick={handlePublish}
          iconName="folder-open"
          label={'Publish'}
          disabled={!dashboardEntityPermissions.moveAndPublish}
          tooltipOption={{
            content: dashboardEntityPermissionsHint.moveAndPublish,
          }}
        />
      )}
      {(variant === DashboardNavSectionType.Published ||
        (variant === DashboardNavSectionType.Shared &&
          template.isPublished)) && (
        <>
          <SingleSelectOption
            onClick={handleUnpublish}
            iconName="folder-minus"
            label={'Unpublish'}
            disabled={!dashboardEntityPermissions.moveAndPublish}
            tooltipOption={{
              content: dashboardEntityPermissionsHint.moveAndPublish,
            }}
          />
          <SingleSelectOption
            onClick={handleMove}
            iconName="folder-open"
            label={'Move To'}
            disabled={!dashboardEntityPermissions.moveAndPublish}
            tooltipOption={{
              content: dashboardEntityPermissionsHint.moveAndPublish,
            }}
          />
        </>
      )}
      <SingleSelectOption
        onClick={handleDuplicate}
        iconName="duplicate"
        label={'Duplicate'}
        disabled={!dashboardEntityPermissions.duplicateDashboard}
        tooltipOption={{
          content: dashboardEntityPermissionsHint.duplicateDashboard,
        }}
      />
      <ThinDivider layout="horizontal" size="full" />
      <SingleSelectOption
        onClick={handleDelete}
        iconName="trash"
        label={'Delete'}
        disabled={!dashboardEntityPermissions.deleteDashboard}
        tooltipOption={{
          content: dashboardEntityPermissionsHint.deleteDashboard,
        }}
      />
    </StyledActionsMenu>
  );
};

export default DashboardTemplateActions;
